import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Form as FormikForm, Formik } from 'formik';
import { pick } from 'lodash';

import {
  GetManualNotificationsQueryVariables,
  ManualNotificationsItem,
  ManualNotificationsItemUpdateInput,
  useDeleteManualNotificationMutation,
  UserSegmentFragment,
  useUpdateManualNotificationMutation,
} from 'api';
import { useNotify } from 'hooks';
import { ManualNotificationSchema } from 'features/admin/validations';
import { useDeleteManualNotification, useUpdateManualNotification } from 'features/admin/updaters';

import Form from './Form';

interface IProps {
  item: ManualNotificationsItem;
  segments: UserSegmentFragment[];
  queryVariables: GetManualNotificationsQueryVariables;
  onEdit(): void;
  onDelete(): void;
}

const EditMessage: FC<IProps> = ({ segments, item, onEdit, queryVariables }) => {
  const { t } = useTranslation();
  const notify = useNotify();

  const deleteMutationOptions = useDeleteManualNotification(queryVariables, {
    onMutateCompleted: () => {
      onEdit();
    },
  });
  const { mutate: deleteManualNotificationMuatation } = useDeleteManualNotificationMutation({
    onSuccess: handleDeleteCompleted,
    ...deleteMutationOptions,
  });

  const updateMutationOptions = useUpdateManualNotification(queryVariables, {
    onMutateCompleted: () => {
      onEdit();
    },
  });

  const { mutate: updateManualNotificationMutation } = useUpdateManualNotificationMutation({
    onSuccess: handleUpdateCompleted,
    ...updateMutationOptions,
  });

  function handleUpdateCompleted() {
    notify.info({ message: t('messages:updateManualNotificationMessageSuccess') });
  }

  function handleDeleteCompleted() {
    notify.info({ message: t('messages:deleteManualNotificationMessageSuccess') });
  }

  function handleEdit(values: ManualNotificationsItemUpdateInput) {
    values.message.url = values.message.url || null;

    const data = pick<ManualNotificationsItemUpdateInput>(
      values,
      'id',
      'entire_company',
      'notification_type',
      'send_at',
      'segment_ids',
      'format',
      'message',
      'email'
    ) as ManualNotificationsItemUpdateInput;

    return updateManualNotificationMutation({ data });
  }

  function handleDelete(id: string) {
    deleteManualNotificationMuatation({ id });
  }

  return (
    <Formik onSubmit={handleEdit} initialValues={item} validationSchema={ManualNotificationSchema}>
      <FormikForm>
        <Form
          titleI18nKey="messages:updateCompanyMessage"
          segments={segments}
          onCancel={onEdit}
          onDelete={handleDelete}
        />
      </FormikForm>
    </Formik>
  );
};

export default EditMessage;
